import jsPDF from 'jspdf';
import openSansRegular from 'open-sans-fonts/open-sans/Regular/OpenSans-Regular.ttf';
import openSansBold from 'open-sans-fonts/open-sans/Bold/OpenSans-Bold.ttf';

const MARGIN = 16;
const PAGE_BOTTOM = 282;
const LINE_HEIGHT = 5.2;
const BOX_SIZE = 3.8;

// jsPDF's built-in fonts have no č, so an embedded font is needed for Slovene text.
const loadFontBase64 = async (url) => {
  const bytes = new Uint8Array(await (await fetch(url)).arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return window.btoa(binary);
};

let fontsPromise = null;
const loadFonts = () => {
  if (!fontsPromise) {
    fontsPromise = Promise.all([loadFontBase64(openSansRegular), loadFontBase64(openSansBold)]).catch((err) => {
      fontsPromise = null;
      throw err;
    });
  }
  return fontsPromise;
};

const fileNameFor = (name) => {
  const slug = String(name).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${slug || 'checklist'}.pdf`;
};

// A printable checklist: every title with its tasks, each with a box to tick by hand.
export const exportChecklistPdf = async ({ name, propertyName, details, rooms }) => {
  const [regular, bold] = await loadFonts();
  const doc = new jsPDF();
  doc.addFileToVFS('OpenSans-Regular.ttf', regular);
  doc.addFont('OpenSans-Regular.ttf', 'OpenSans', 'normal');
  doc.addFileToVFS('OpenSans-Bold.ttf', bold);
  doc.addFont('OpenSans-Bold.ttf', 'OpenSans', 'bold');

  const textWidth = doc.internal.pageSize.getWidth() - MARGIN * 2;
  let y = 20;
  const newPageIfNeeded = (needed) => {
    if (y + needed > PAGE_BOTTOM) {
      doc.addPage();
      y = 18;
    }
  };

  doc.setFont('OpenSans', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(30, 41, 59);
  doc.splitTextToSize(name, textWidth).forEach((line) => {
    doc.text(line, MARGIN, y);
    y += 8;
  });

  doc.setFont('OpenSans', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  [propertyName, ...details].filter(Boolean).forEach((line) => {
    doc.splitTextToSize(line, textWidth).forEach((part) => {
      doc.text(part, MARGIN, y);
      y += 5;
    });
  });
  y += 5;

  rooms.forEach((room) => {
    // Keep a title together with its first task.
    newPageIfNeeded(18);
    doc.setFont('OpenSans', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(30, 41, 59);
    doc.text(room.name, MARGIN, y);
    y += 2;
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, y, MARGIN + textWidth, y);
    y += 6;

    doc.setFont('OpenSans', 'normal');
    doc.setFontSize(10.5);
    (room.tasks || []).forEach((task) => {
      const label = task.requires_photo ? `${task.title} (photo required)` : task.title;
      const lines = doc.splitTextToSize(label, textWidth - BOX_SIZE - 3);
      newPageIfNeeded(lines.length * LINE_HEIGHT + 2);
      doc.setDrawColor(100, 116, 139);
      doc.setLineWidth(0.3);
      doc.rect(MARGIN, y - BOX_SIZE + 0.6, BOX_SIZE, BOX_SIZE);
      lines.forEach((line, index) => {
        doc.text(line, MARGIN + BOX_SIZE + 3, y + index * LINE_HEIGHT);
      });
      y += lines.length * LINE_HEIGHT + 2;
    });
    y += 5;
  });

  doc.save(fileNameFor(name));
};
